'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ContactSalesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactSalesModal: React.FC<ContactSalesModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    businessEmail: '',
    phoneNumber: '',
    company: '',
    jobTitle: '',
    industry: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1000));

    setIsSubmitting(false);
    setSubmitSuccess(true);

    // Reset form after 2 seconds and close modal
    setTimeout(() => {
      setFormData({
        name: '',
        businessEmail: '',
        phoneNumber: '',
        company: '',
        jobTitle: '',
        industry: ''
      });
      setSubmitSuccess(false);
      onClose();
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-primary/80 backdrop-blur-sm"
        onClick={onClose} />

      
      {/* Modal */}
      <div className="relative bg-card rounded-lg shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-primary via-secondary to-brand-teal px-6 py-3 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon name="ChatBubbleLeftRightIcon" size={32} variant="solid" className="text-white" />
              <h2 className="font-display font-bold text-2xl text-white">
                Contact Sales
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-accent transition-colors duration-200"
              aria-label="Close modal">

              <Icon name="XMarkIcon" size={28} variant="solid" />
            </button>
          </div>
          <p className="font-body text-white/90 mt-2">
            Let's discuss how UnifyMax can transform your customer engagement
          </p>
        </div>

        {/* Form */}
        <div className="p-6">
          {submitSuccess ?
          <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-4">
                <Icon name="CheckCircleIcon" size={40} variant="solid" className="text-success" />
              </div>
              <h3 className="font-display font-bold text-2xl text-primary mb-2">
                Thank You!
              </h3>
              <p className="font-body text-muted-foreground">
                We'll be in touch shortly to discuss your needs.
              </p>
            </div> :

          <form onSubmit={handleSubmit}>
            <div className="gap-4 grid grid-cols-1  md:grid-cols-2"> 
              {/* Name */}
              <div>
                <label htmlFor="name" className="block font-body font-medium text-primary mb-2">
                  Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-md font-body text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                  placeholder="John Doe" />

              </div>

              {/* Business Email */}
              <div>
                <label htmlFor="businessEmail" className="block font-body font-medium text-primary mb-2">
                  Business Email <span className="text-error">*</span>
                </label>
                <input
                  type="email"
                  id="businessEmail"
                  name="businessEmail"
                  value={formData.businessEmail}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-md font-body text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                  placeholder="john.doe@company.com" />

              </div>

              {/* Phone Number */}
              <div>
                <label htmlFor="phoneNumber" className="block font-body font-medium text-primary mb-2">
                  Phone Number <span className="text-error">*</span>
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-md font-body text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                  placeholder="+1 (555) 123-4567" />

              </div>

              {/* Company */}
              <div>
                <label htmlFor="company" className="block font-body font-medium text-primary mb-2">
                  Company <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-md font-body text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                  placeholder="Your Company Name" />

              </div>

              {/* Job Title */}
              <div>
                <label htmlFor="jobTitle" className="block font-body font-medium text-primary mb-2">
                  Job Title <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  id="jobTitle"
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-md font-body text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                  placeholder="Chief Technology Officer" />

              </div>

              {/* Industry */}
              <div>
                <label htmlFor="industry" className="block font-body font-medium text-primary mb-2">
                  Industry <span className="text-error">*</span>
                </label>
                <select
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-md font-body text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200">

                  <option value="">Select your industry</option>
                  <option value="telecom">Telecommunications</option>
                  <option value="banking">Banking & Financial Services</option>
                  <option value="retail">Retail & E-commerce</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="insurance">Insurance</option>
                  <option value="technology">Technology</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="other">Other</option>
                </select>
              </div>

              

              
              </div>
              <div className="pt-4 mt-5 border-t border-border"></div>
                  <div className="gap-4 grid grid-cols-1">
                  {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 bg-gradient-to-r from-conversion to-warning text-conversion-foreground rounded-md font-cta text-md font-semibold hover:shadow-cta transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2">

                  {isSubmitting ?
                  <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Submitting...</span>
                    </> :

                  <>
                      <Icon name="PaperAirplaneIcon" size={20} variant="solid" />
                      <span>Submit Request</span>
                    </>
                  }
                </button>
              </div>
                {/* Trust Indicators */}
              <div className="w-full mt-4 flex items-center justify-center space-x-4 ">
                <div className="flex items-center space-x-1">
                  <Icon name="ShieldCheckIcon" size={16} variant="solid" className="text-success" />
                  <span className="font-body text-xs text-muted-foreground">Secure</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="ClockIcon" size={16} variant="solid" className="text-brand-teal" />
                  <span className="font-body text-xs text-muted-foreground">Quick Response</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="CheckBadgeIcon" size={16} variant="solid" className="text-primary" />
                  <span className="font-body text-xs text-muted-foreground">No Commitment</span>
                </div>
              </div>
               </div>
            
            </form>
          }
        </div>
      </div>
    </div>);

};

export default ContactSalesModal;